import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useEffect, useState } from "react";
import Button from "../../components/Button/Button";
import Input from "../../components/Input/Input";
import RadioOption from "../../components/RadioOption/RadioOption";
import styles from "./CreateListing.module.css";
import schema from "../../utils/validations/CreateListing";
import { AppDispatch, RootState } from "../../store";
import { useDispatch, useSelector } from "react-redux";

import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router";
import { updateListingThunk } from "../../store/listing/updatingListingThunk";
import { useGetSpecificListingQuery } from "../../store/listing/fetchSpecialListing";
import ClipLoader from "react-spinners/esm/ClipLoader";
import loadingStyles from "../../shared-styles/loading.module.css";
export type CreateListingFormData = yup.InferType<typeof schema>;

export default function EditListing() {
  const { id } = useParams();

  const navigate = useNavigate();

  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [imagePreviewUrls, setImagePreviewUrls] = useState<string[]>([]);

  const { uid } = useSelector((state: RootState) => state.auth.user);
  const dispatch = useDispatch<AppDispatch>();

  const {
    data: currentListing,
    isLoading: fetchListingLoading,
    isError: fetchListingError
  } = useGetSpecificListingQuery(id ?? "");

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
    setValue
  } = useForm<CreateListingFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      sellRent: "sell",
      parkingSpot: "no",
      furnished: "no",
      offer: "no"
    }
  });

  const sellRentValue = watch("sellRent");
  const parkingSpotValue = watch("parkingSpot");
  const furnishedValue = watch("furnished");
  const offerValue = watch("offer");

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const fileArray = Array.from(files);
      setSelectedImages(fileArray);

      const urls = fileArray.map((file) => URL.createObjectURL(file));
      setImagePreviewUrls(urls);

      setValue("images", files);
    }
  };

  const removeImage = (index: number) => {
    const newImages = selectedImages.filter((_, i) => i !== index);
    const newUrls = imagePreviewUrls.filter((_, i) => i !== index);

    setSelectedImages(newImages);
    setImagePreviewUrls(newUrls);

    const dataTransfer = new DataTransfer();

    newImages.forEach((image) => dataTransfer.items.add(image));

    setValue("images", dataTransfer.files);
  };

  const onSubmit = async (data: CreateListingFormData) => {
    const userListing = {
      ...data
    };
    try {
      console.log(selectedImages);
      const resultAction = await dispatch(
        updateListingThunk({
          item: userListing,
          uid: String(uid),
          id: String(id),
          images: [...selectedImages]
        })
      );
      if (updateListingThunk.rejected.match(resultAction)) {
        toast.error(resultAction.payload?.message);
      } else {
        toast.success("Listing Updated Successfully!");
        const timeout = setTimeout(() => {
          navigate("/profile");
          clearTimeout(timeout);
        }, 1000);
      }
    } catch (error) {
      toast.error("Something went wrong!");
    }
  };

  // Set form values when currentListing changes
  useEffect(() => {
    if (!currentListing) return;

    const listingData = {
      propertyName: currentListing?.fields?.propertyName?.stringValue || "",
      beds: Number(currentListing?.fields?.beds?.integerValue) || 0,
      baths: Number(currentListing?.fields?.baths?.integerValue) || 0,
      address: currentListing?.fields?.address?.stringValue || "",
      description: currentListing?.fields?.description?.stringValue || "",
      price: Number(currentListing?.fields?.price?.integerValue) || 0,
      sellRent: currentListing?.fields?.sellRent?.stringValue || "sell",
      parkingSpot: currentListing?.fields?.parkingSpot?.stringValue || "no",
      furnished: currentListing?.fields?.furnished?.stringValue || "no",
      offer: currentListing?.fields?.offer?.stringValue || "no",
      images: currentListing?.fields?.images?.arrayValue?.values || []
    };

    const allImages = currentListing?.fields?.images?.arrayValue?.values.map(
      (image: { stringValue: string }) => {
        return image.stringValue;
      }
    );
    console.log("allImages" + allImages);
    setSelectedImages(allImages);
    // Set form values
    Object.entries(listingData).forEach(([key, value]) =>
      setValue(key as keyof CreateListingFormData, value as any)
    );

    // Set image previews
    if (currentListing?.fields?.images?.arrayValue?.values) {
      const imageUrls = currentListing.fields.images.arrayValue.values.map(
        (img: { stringValue: string }) => img.stringValue
      );
      console.log("imageUrls:", imageUrls);
      setImagePreviewUrls(imageUrls);
    }
  }, [currentListing, setValue]);

  if (fetchListingLoading)
    return (
      <div className={loadingStyles.loading}>
        <ClipLoader loading size={200}  color="#35a257"/>
      </div>
    );
  if (fetchListingError) {
    return <div>Error fetching listings</div>;
  }
  if (!currentListing) {
    return <div>Listing not found</div>;
  }

  return (
    <main className={styles.main}>
      <div className="container">
        <div className={styles.header}>
          <h1>Create New Listing</h1>
          <p>Add your property details below</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
          <div className={styles.formGrid}>
            <div className={styles.section}>
              <h3>Basic Information</h3>

              <div className={styles.radioGroup}>
                <RadioOption
                  label="Sell / Rent"
                  name="sellRent"
                  options={[
                    { value: "sell", label: "Sell" },
                    { value: "rent", label: "Rent" }
                  ]}
                  value={sellRentValue}
                  onChange={(value) => setValue("sellRent", value)}
                />
                {errors.sellRent && (
                  <span className={styles.errorText}>
                    {errors.sellRent.message}
                  </span>
                )}
              </div>

              <div className={styles.inputGroup}>
                <Input
                  label="Property Name"
                  type="text"
                  placeholder="Enter property name"
                  errorMessage={errors.propertyName?.message}
                  {...register("propertyName")}
                />
              </div>

              <div className={styles.row}>
                <div className={styles.inputGroup}>
                  <Input
                    label="Beds"
                    type="number"
                    min="1"
                    errorMessage={errors.beds?.message}
                    {...register("beds", { valueAsNumber: true })}
                  />
                </div>
                <div className={styles.inputGroup}>
                  <Input
                    label="Baths"
                    type="number"
                    min="1"
                    errorMessage={errors.baths?.message}
                    {...register("baths", { valueAsNumber: true })}
                  />
                </div>
              </div>
            </div>

            <div className={styles.section}>
              <h3>Property Features</h3>

              <div className={styles.radioGroup}>
                <RadioOption
                  label="Parking Spot"
                  name="parkingSpot"
                  options={[
                    { value: "yes", label: "Yes" },
                    { value: "no", label: "No" }
                  ]}
                  value={parkingSpotValue}
                  onChange={(value) => setValue("parkingSpot", value)}
                />
                {errors.parkingSpot && (
                  <span className={styles.errorText}>
                    {errors.parkingSpot.message}
                  </span>
                )}
              </div>

              <div className={styles.radioGroup}>
                <RadioOption
                  label="Furnished"
                  name="furnished"
                  options={[
                    { value: "yes", label: "Yes" },
                    { value: "no", label: "No" }
                  ]}
                  value={furnishedValue}
                  onChange={(value) => setValue("furnished", value)}
                />
                {errors.furnished && (
                  <span className={styles.errorText}>
                    {errors.furnished.message}
                  </span>
                )}
              </div>
            </div>

            <div className={styles.section}>
              <h3>Location & Description</h3>

              <div className={styles.inputGroup}>
                <label htmlFor="address">Address</label>
                <textarea
                  id="address"
                  placeholder="Enter property address"
                  className={styles.textarea}
                  {...register("address")}
                />
                {errors.address && (
                  <span className={styles.errorText}>
                    {errors.address.message}
                  </span>
                )}
              </div>

              <div className={styles.inputGroup}>
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  placeholder="Describe your property"
                  className={styles.textarea}
                  rows={4}
                  {...register("description")}
                />
                {errors.description && (
                  <span className={styles.errorText}>
                    {errors.description.message}
                  </span>
                )}
              </div>
            </div>

            <div className={styles.section}>
              <h3>Pricing & Offers</h3>

              <div className={styles.radioGroup}>
                <RadioOption
                  label="Offer"
                  name="offer"
                  options={[
                    { value: "yes", label: "Yes" },
                    { value: "no", label: "No" }
                  ]}
                  value={offerValue}
                  onChange={(value) => setValue("offer", value)}
                />
                {errors.offer && (
                  <span className={styles.errorText}>
                    {errors.offer.message}
                  </span>
                )}
              </div>

              <div className={styles.inputGroup}>
                <Input
                  label="Price"
                  type="number"
                  placeholder={watch("sellRent") === "rent" ? "$/Month" : "$"}
                  min="1"
                  errorMessage={errors.price?.message}
                  {...register("price", { valueAsNumber: true })}
                />
              </div>
            </div>

            <div className={styles.section}>
              <h3>Images</h3>
              <p className={styles.imageNote}>
                The first image will be the cover (max 6 images)
              </p>

              {/* Image Previews */}
              {imagePreviewUrls.length > 0 && (
                <div className={styles.imagePreviews}>
                  {imagePreviewUrls.map((url, index) => (
                    <div key={index} className={styles.imagePreview}>
                      <img src={url} alt={`Preview ${index + 1}`} />
                      <button
                        type="button"
                        className={styles.removeImage}
                        onClick={() => removeImage(index)}
                        disabled={isSubmitting}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className={styles.fileUpload}>
                <div className={styles.uploadArea}>
                  <div className={styles.uploadIcon}>
                    <svg
                      width="48"
                      height="48"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12 16L12 8M12 8L15 11M12 8L9 11"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M3 15V16C3 18.8284 3 20.2426 3.87868 21.1213C4.75736 22 6.17157 22 9 22H15C17.8284 22 19.2426 22 20.1213 21.1213C21 20.2426 21 18.8284 21 16V15C21 12.1716 21 10.7574 20.1213 9.87868C19.2426 9 17.8284 9 15 9H14C13.6494 9 13.4731 9 13.3054 8.94444C13.1378 8.88889 12.9906 8.77778 12.6964 8.55556L10.3036 6.44444C10.0094 6.22222 9.86225 6.11111 9.69461 6.05556C9.52697 6 9.35064 6 9 6H9C6.17157 6 4.75736 6 3.87868 6.87868C3 7.75736 3 9.17157 3 12V15Z"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                    </svg>
                  </div>
                  <div className={styles.uploadText}>
                    <span className={styles.uploadTitle}>
                      {selectedImages.length > 0
                        ? `${selectedImages.length} file(s) selected`
                        : "Choose Files"}
                    </span>
                    <span className={styles.uploadSubtitle}>
                      {selectedImages.length === 0
                        ? "No file chosen"
                        : "Click to change"}
                    </span>
                  </div>
                  <input
                    type="file"
                    id="images"
                    multiple
                    accept="image/*"
                    className={styles.fileInput}
                    onChange={handleImageChange}
                  />
                </div>
                {errors.images && selectedImages.length < 1 && (
                  <span className={styles.errorText}>
                    {errors.images.message}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className={styles.submitSection}>
            <Button type="submit" className="primary" disabled={isSubmitting}>
              {isSubmitting ? "Updateing..." : "Updateing"}
            </Button>
          </div>
        </form>
      </div>
    </main>
  );
}
